"use client";

import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon, PlusIcon, SettingsIcon } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formModal } from "@/components/admin/FormModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useActiveBranch } from "@/hooks/use-active-branch";
import { useFile } from "@/hooks/use-file";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { cn, getFullName, getInitials } from "@/lib/utils";
import { BranchForm } from "@/modules/branches/form";
import { branchesQueries } from "@/modules/branches/queries";
import { useSession } from "@/providers/session-provider";

export function BranchSwitcher() {
	const { isMobile } = useSidebar();
	const { t } = useTranslation();
	const { getUrl } = useFile();
	const { activeBranchId, setActiveBranch } = useActiveBranch();
	const session = useSession();

	const branches = useQuery(branchesQueries.fullList());

	const activeBranch = useMemo(
		() => branches.data?.find((branch) => branch.id === activeBranchId),
		[branches.data, activeBranchId]
	);

	const { isBlocked, showBlockedModal } = useSubscriptionStatus();

	const activeBranchImageUrl =
		activeBranch &&
		getUrl({
			collectionName: "Branches",
			id: activeBranch.id,
			filename: activeBranch.image,
		});

	const getBranchName = (name?: string) => {
		if (!name) {
			return t(($) => $.common.components.sidebar.branches.label);
		}

		if (name === "main") {
			return t(($) => $.modules.branches.terms.main);
		}

		return name;
	};

	const onCreate = useCallback(() => {
		if (isBlocked) {
			showBlockedModal();
			return;
		}

		formModal.open({
			title: t(($) => $.modules.branches.forms.create.title),
			description: t(($) => $.modules.branches.forms.create.description),
			form: <BranchForm onCancel={formModal.close} onSuccess={formModal.close} />,
		});
	}, [t, isBlocked, showBlockedModal]);

	const branchName = getBranchName(activeBranch?.name);

	const sidebarMenuButtonRef = useRef<HTMLButtonElement>(null);

	const onSettinsClick = useCallback(() => {
		formModal.open({
			title: t(($) => $.modules.branches.forms.edit.title),
			description: t(($) => $.modules.branches.forms.edit.description),
			form: (
				<BranchForm
					id={activeBranch?.id}
					onCancel={formModal.close}
					onSuccess={formModal.close}
					readOnly={activeBranch?.doctor?.id !== session?.id}
				/>
			),
		});
	}, [t, activeBranch?.id, activeBranch?.doctor?.id, session?.id]);

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							className={cn(
								"cursor-pointer",
								"data-[state=open]:rounded-b-none",
								"data-[state=open]:border",
								"data-[state=open]:border-border",
								"data-[state=open]:border-b-0",
								"data-[state=open]:bg-background",
								"data-[state=open]:text-sidebar-accent-foreground"
							)}
							ref={sidebarMenuButtonRef}
							size="lg"
						>
							<Avatar>
								<AvatarImage alt={branchName} className="object-cover" src={activeBranchImageUrl} />
								<AvatarFallback>{getInitials(branchName)}</AvatarFallback>
							</Avatar>

							<div className="grid flex-1 text-left text-sm/tight">
								<span className="truncate font-semibold">{branchName}</span>
								<span className="truncate text-xs">
									{activeBranch ? activeBranch.address : t(($) => $.common.components.sidebar.branches.buttons.select)}
								</span>
							</div>

							<ChevronDownIcon className="ml-auto" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>

					<DropdownMenuContent
						align="start"
						className="-mt-1 rounded-lg rounded-t-none border-t-0"
						ref={(el) => {
							if (!(el && sidebarMenuButtonRef.current)) return;
							el.style.width = `${sidebarMenuButtonRef.current.offsetWidth}px`;
						}}
						side={isMobile ? "bottom" : "bottom"}
						sideOffset={4}
					>
						<DropdownMenuGroup className="mx-1 mb-2 flex items-center justify-between">
							<DropdownMenuItem asChild>
								<Button
									className="w-full cursor-pointer text-muted-foreground text-xs"
									onClick={onSettinsClick}
									size={"sm"}
									variant={"outline"}
								>
									<SettingsIcon className="size-4" />
									<span>{t(($) => $.modules.branches.terms.settings)}</span>
								</Button>
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />

						<DropdownMenuGroup>
							<DropdownMenuLabel className="text-muted-foreground text-xs">
								{t(($) => $.common.components.sidebar.branches.buttons.select)}
							</DropdownMenuLabel>

							{branches.data?.map((branch) => {
								const imageUrl = getUrl({
									collectionName: "Branches",
									id: branch.id,
									filename: branch.image,
								});

								const isOwner = branch.doctor.id === session?.id;

								return (
									<DropdownMenuItem
										className={cn("cursor-pointer gap-2 p-2", activeBranch?.id === branch.id && "bg-muted")}
										key={branch.id}
										onClick={() => setActiveBranch(branch.id)}
									>
										<Avatar>
											<AvatarImage alt={branch.name} className="object-cover" src={imageUrl} />
											<AvatarFallback>{getInitials(getBranchName(branch.name))}</AvatarFallback>
										</Avatar>

										<div className="flex-1 truncate">
											<p className="truncate font-medium" title={getBranchName(branch.name)}>
												{getBranchName(branch.name)}
											</p>{" "}
											<small className="text-muted-foreground text-xs" title={getFullName(branch.doctor)}>
												{getFullName(branch.doctor)}
											</small>
										</div>

										<div>
											<small className="text-muted-foreground text-xs">
												{isOwner ? t(($) => $.modules.branches.terms.owner) : t(($) => $.modules.branches.terms.guest)}
											</small>
										</div>
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuGroup>

						<DropdownMenuItem className="mt-2 cursor-pointer gap-2" onClick={onCreate}>
							<div className="flex size-6 items-center justify-center rounded-md border bg-background">
								<PlusIcon className="size-4" />
							</div>

							<div className="font-medium text-muted-foreground">
								{t(($) => $.common.components.sidebar.branches.buttons.add)}
							</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
